/**
 * Batched product catalog layers — ONE request for a whole document's lines.
 *
 * `ProductReferencePicker` renders once per line and each instance owned five
 * queries: the product itself, its packagings, its variant dimensions, its
 * variants and its effective configurations. Those go out in PARALLEL, and each
 * one holds a backend request-pool connection for its whole handler duration —
 * production's ceiling is 15 (`_REQUEST_POOL_SIZE + _REQUEST_MAX_OVERFLOW` in
 * `apps/backend/app.py`), so the arithmetic that matters is lines x concurrent
 * users. A 21-line quote measured 105 parallel requests.
 *
 * This is the house answer from `.claude/rules/rules-frontend.md` § "A component
 * rendered ONCE PER CATALOG ENTRY must not own its own query": the page gets ONE
 * endpoint over the whole set and hands each child its slice through context, so
 * the child stays reusable on its own page. `BulkNotificationPreferencesContext`
 * (`shared/notifications/useNotificationPreferences.ts`) is the worked example —
 * the provider is the batched mount, its ABSENCE is the single mount.
 *
 * A line-item editor wraps its rows in `ProductCatalogLayersProvider`; a picker
 * mounted on its own (a filter, a ticket's related product, a single dialog)
 * finds no provider and keeps its own per-product queries, unchanged.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { loadProductCatalogLayersApiV1MasterDataProductsProductCatalogLayersPost } from '../../../../generated/api/products/products';
import { productCatalogLayerKeys } from './productCatalogLayerKeys';
import type {
  Product,
  ProductConfiguration,
  ProductDimension,
  ProductPackaging,
  ProductVariant,
} from '../types';
import { useProduct, useProductVariants } from './useProducts';
import { useProductPackagings } from './usePackagings';
import { useProductDimensions } from './useDimensions';
import { useProductConfigurations } from './useConfigurations';

/** Mirrors the backend cap on `ProductCatalogLayersRequest.product_ids`. */
export const MAX_CATALOG_LAYER_PRODUCTS = 200;

export { productCatalogLayerKeys };

/** One product's sub-layer data — the batched twin of the five per-product reads.
 *  Named apart from the generated wire model `ProductCatalogLayers`: this is the
 *  module's own typed view (the wire model's layer arrays are pass-through
 *  objects) and it carries no `product_id`, which the map key already is. */
export interface ProductReferenceLayers {
  product: Product | null;
  packagings: ProductPackaging[];
  dimensions: ProductDimension[];
  variants: ProductVariant[];
  configurations: ProductConfiguration[];
}

/** One wire row, read back through the module's own types (the generated model
 *  types each layer array as a pass-through object — see the provider below). */
interface ProductCatalogLayersEntry extends ProductReferenceLayers {
  product_id: string;
}

// Stable empty identities: a fresh `[]` per render would re-run every consumer's
// memo, and these are handed straight to `useMemo` dependency lists.
const NO_PACKAGINGS: ProductPackaging[] = [];
const NO_DIMENSIONS: ProductDimension[] = [];
const NO_VARIANTS: ProductVariant[] = [];
const NO_CONFIGURATIONS: ProductConfiguration[] = [];

/**
 * Published by the batched mount. `null` (no provider) means "single mount" —
 * every picker below keeps its own per-product queries.
 */
const ProductCatalogLayersContext = createContext<Map<string, ProductReferenceLayers> | null>(
  null,
);

interface ProductCatalogLayersProviderProps {
  /** Every line's `product_id`; nulls, blanks and duplicates are dropped here. */
  productIds: (string | null | undefined)[];
  /**
   * Mount the batch at all. `false` renders the children with NO provider and
   * NO query — for an editor whose product column is conditional, where there
   * are no pickers below to batch and so nothing to fetch. Default `true`.
   */
  enabled?: boolean;
  children: ReactNode;
}

/**
 * Resolve every line's catalog layers in ONE request. Mount ONCE, around a
 * line-item editor's rows.
 *
 * Fetched through the generated orval client, so the URL, verb and request body
 * cannot drift from the route. Only the LAYER ARRAYS are read back through this
 * module's own types: the route declares them as pass-through objects (the
 * per-product routes they mirror are untyped, and their JSON Schemas have drifted
 * from their serializers — see the schema's own description), so the generated
 * model types them `Record<string, unknown>[]` and every consumer would cast
 * anyway. The types cast to are exactly what the four per-product hooks in this
 * directory already return.
 */
export function ProductCatalogLayersProvider({
  productIds,
  enabled = true,
  children,
}: ProductCatalogLayersProviderProps) {
  // Split rather than branching inside: `useQuery` may not be called
  // conditionally, and a disabled batch must own no query at all — not merely a
  // disabled one, which still demands a QueryClient from every host.
  if (!enabled) return <>{children}</>;
  return <BatchedCatalogLayersMount productIds={productIds}>{children}</BatchedCatalogLayersMount>;
}

function BatchedCatalogLayersMount({
  productIds,
  children,
}: Omit<ProductCatalogLayersProviderProps, 'enabled'>) {
  // Sorted + de-duplicated so the query key is stable across row reorders and a
  // re-render that produces the same set does not refetch.
  const ids = useMemo(() => {
    const unique = new Set<string>();
    for (const id of productIds) {
      if (id) unique.add(id);
    }
    return Array.from(unique).sort().slice(0, MAX_CATALOG_LAYER_PRODUCTS);
  }, [productIds]);

  const { data } = useQuery({
    queryKey: productCatalogLayerKeys.byProducts(ids),
    queryFn: async ({ signal }) => {
      const response =
        await loadProductCatalogLayersApiV1MasterDataProductsProductCatalogLayersPost(
          { product_ids: ids },
          signal,
        );
      return response.data as unknown as ProductCatalogLayersEntry[];
    },
    enabled: ids.length > 0,
  });

  const byProduct = useMemo(() => {
    const map = new Map<string, ProductReferenceLayers>();
    for (const entry of data ?? []) {
      map.set(entry.product_id, {
        product: entry.product ?? null,
        packagings: entry.packagings ?? NO_PACKAGINGS,
        dimensions: entry.dimensions ?? NO_DIMENSIONS,
        variants: entry.variants ?? NO_VARIANTS,
        configurations: entry.configurations ?? NO_CONFIGURATIONS,
      });
    }
    return map;
  }, [data]);

  return (
    <ProductCatalogLayersContext.Provider value={byProduct}>
      {children}
    </ProductCatalogLayersContext.Provider>
  );
}

/**
 * This product's slice of the batched read.
 *
 * `batched` says whether a provider is above — which is what a consumer needs in
 * order to DISABLE its own query. While the batch is in flight `slice` is null,
 * which renders exactly as a per-product query that has not resolved yet does.
 */
export function useProductCatalogLayerSlice(productId: string | null | undefined): {
  batched: boolean;
  slice: ProductReferenceLayers | null;
} {
  const byProduct = useContext(ProductCatalogLayersContext);
  return {
    batched: byProduct !== null,
    slice: byProduct && productId ? byProduct.get(productId) ?? null : null,
  };
}

/** Which of the picker's sub-layer reads can actually reach the screen. */
export interface ProductReferenceLayerGates {
  packagings: boolean;
  variants: boolean;
  configurations: boolean;
}

/**
 * The four sub-layer arrays a `ProductReferencePicker` renders from — served
 * from the batched mount when there is one, and from the per-product queries
 * when there is not.
 *
 * The gates are the second half of the fix: a read whose layer CANNOT render
 * (the picker is `productOnly`, or the tenant has variants / configurations
 * switched off) is pure waste, and on a single mount it is waste paid per line.
 */
export function useProductReferenceLayers(
  productId: string | null,
  gates: ProductReferenceLayerGates,
): Omit<ProductReferenceLayers, 'product'> {
  const { batched, slice } = useProductCatalogLayerSlice(productId);
  // An empty id disables a per-product query — that is how all four hooks spell
  // "nothing to fetch", so it is also how a batched mount switches them off.
  const soloId = batched ? '' : productId ?? '';

  const { data: packagings } = useProductPackagings(gates.packagings ? soloId : '');
  const { data: dimensions } = useProductDimensions(gates.variants ? soloId : '');
  const { data: variants } = useProductVariants(gates.variants ? soloId : '');
  const { data: configurations } = useProductConfigurations(
    gates.configurations ? soloId : '',
  );

  return useMemo(() => {
    if (batched) {
      return {
        packagings: gates.packagings ? slice?.packagings ?? NO_PACKAGINGS : NO_PACKAGINGS,
        dimensions: gates.variants ? slice?.dimensions ?? NO_DIMENSIONS : NO_DIMENSIONS,
        variants: gates.variants ? slice?.variants ?? NO_VARIANTS : NO_VARIANTS,
        configurations: gates.configurations
          ? slice?.configurations ?? NO_CONFIGURATIONS
          : NO_CONFIGURATIONS,
      };
    }
    return {
      packagings: packagings ?? NO_PACKAGINGS,
      dimensions: dimensions ?? NO_DIMENSIONS,
      variants: variants ?? NO_VARIANTS,
      configurations: configurations ?? NO_CONFIGURATIONS,
    };
  }, [
    batched,
    slice,
    gates.packagings,
    gates.variants,
    gates.configurations,
    packagings,
    dimensions,
    variants,
    configurations,
  ]);
}

/**
 * The product a picker's trigger labels itself with when the catalog list does
 * not carry it — a custom one-off, which `GET /products` deliberately omits.
 *
 * Under a batched mount the row is already in hand; standalone it is the same
 * single `GET /products/{id}` the picker has always issued.
 */
export function useProductReferenceFallbackProduct(
  productId: string | null,
  /** False once the catalog list itself has the product — nothing to fetch. */
  needed: boolean,
): Product | null {
  const { batched, slice } = useProductCatalogLayerSlice(productId);
  const { data: standalone } = useProduct(
    !batched && needed && productId ? productId : '',
  );
  if (batched) return needed ? slice?.product ?? null : null;
  return standalone ?? null;
}
